import type { FC } from "react";
import dayjs from "dayjs";
import Link from "next/link";

import type {
  TArticleCardProps,
} from "@/ui/components/data-display/Card/ArticleCard/ArticleCard.config";
import { HorizontalRule, Inline, Stack } from "@/ui/components/layout";
import { AspectRatio, Badge, Image } from "@/ui/components/primitives";
import { TextBody } from "@/ui/components/typography/TextBody";
import { TextControl } from "@/ui/components/typography/TextControl";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";
import { SHORTHAND_MONTH_DAY_YEAR } from "libs/utils/misc/src/dates";
import { useTranslations } from "next-intl";

export const ArticleCard: FC<TArticleCardProps> = ({
  image,
  categories,
  title,
  description,
  publishedAt,
  readTime,
  author,
  href,
}) => {

  const t = useTranslations("Articles");

  return (
    <article
      data-component="ArticleCard"
      className="rounded-sys-sm border border-solid border-component-card-base overflow-hidden shadow-sm"
    >
      <AspectRatio ratio="4:3">
        <Image {...image} fit="cover" />
      </AspectRatio>
      <Stack gap="4" className="p-4">
        <Stack gap="2">
          <Inline gap="2">
            { categories.map((category, index) => (
              <Badge key={index} variant="outline" {...category} />
            )) }
          </Inline>
          <div className="text-foreground-muted-strong">
            <TextBody
              size={3}
              isResponsive={false}
              color="inherit"
            >
              { dayjs(publishedAt).format(SHORTHAND_MONTH_DAY_YEAR) } · { t("readTime", { readTime }) }
            </TextBody>
          </div>
          <Link href={href}>
            <TextSubtitle tag="h3" size={2} isResponsive={false}>
              { title }
            </TextSubtitle>
          </Link>
          <TextBody size={3} isResponsive={false}>
            { description }
          </TextBody>
        </Stack>
        <HorizontalRule />
        <Inline gap="2">
          <div className="w-8">
            <AspectRatio ratio="3:4">
              <Image {...author.image} fit="cover" />
            </AspectRatio>
          </div>
          <div className="flex flex-col flex-1 justify-between">
            <TextControl size={2} isResponsive={false}>
              { author.name }
            </TextControl>
            <TextBody size={3} isResponsive={false}>
              { author.description }
            </TextBody>
          </div>
        </Inline>
      </Stack>
    </article>
  );
};

ArticleCard.displayName = "ArticleCard";
